import {
  FUEL_LOW,
  ROAD_CLASSES,
  className,
  formatClock,
  sayDistance,
  sayDuration,
  stateSlogan,
  type County,
  type IncidentRow,
} from '@hotlist/shared';
import type { HudModel, VehicleInfo } from './state.ts';
import { ROAD_COLOR, roadWords } from './theme.ts';

/**
 * The heads up display, in DOM rather than in the renderer.
 *
 * Enforcement plays through a query interface over a database, so the game's
 * own interface being HTML is not a shortcut. It is the same surface the real
 * one has, and building it in the renderer would mean rebuilding text input,
 * scrolling and selection for no gain.
 */
export class Hud {
  private readonly countyName = must('county-name');
  private readonly countyRows = must('county-rows');
  private readonly connectionDot = must('connection-dot');
  private readonly selfPanel = must('self');
  private readonly selfPlate = must('self-plate');
  private readonly selfRows = must('self-rows');
  private readonly help = must('help');
  private readonly statusline = must('statusline');
  private readonly fuel = must('fuel');
  private readonly fuelNeedle = must('fuel-needle');
  private readonly fuelSaid = must('fuel-said');
  /** Whether this player's own plate is on a list. */
  private wanted = false;
  private readonly selection = must('selection');
  private readonly selectionPlate = must('selection-plate');
  private readonly selectionNote = must('selection-note');
  private readonly selectionRows = must('selection-rows');
  private readonly selectionInspect = must('selection-inspect');
  private readonly legend = must('legend');
  private readonly toast = must('toast');

  // The notifications feed: a live collapsible left panel where every toast is
  // also kept, newest first, so a message that flashed by can be read back.
  private readonly notesPanel = must('notes-panel');
  private readonly notesList = must('notes-list');
  private readonly notesPause = must('notes-pause');
  private readonly logHistory: { text: string; at: number }[] = [];
  /** Frozen for reading: messages still bank, the view just does not jump. */
  private notesPaused = false;
  private notesPending = 0;

  private lastPainted = 0;
  private toastTimer = 0;
  /**
   * How long a day is here, which the world decides and is not 24 hours.
   *
   * This panel used to divide by 86,400 while every other timestamp in the
   * interface went through formatClock with the world's own figure, so the HUD
   * read d1 05:25 at the same instant the phone read d14 01:25. Two clocks in
   * one game disagreeing is worse than either being wrong.
   */
  private daySeconds: number | undefined;
  /** The world clock, so an arrival time can be a time rather than a duration. */
  private now = 0;
  /** Which half of the game this is, because the two want different rows. */
  private enforcement = false;

  private readonly county: County;

  /**
   * The administrator's inspector, painted under the ordinary selection rows.
   *
   * The ordinary panel shows what a person at the curb could see. This block
   * is the county operator's truth about the same car -- lights, the call it
   * is out on, where it is headed, whether a person is driving -- with its
   * short trail of recent activity underneath. Kept visually inside the same
   * panel so selecting a car answers every question in one place.
   */
  inspect(told: {
    kind: string;
    lights: boolean;
    call: string;
    headed: string;
    wanted: boolean;
    player: boolean;
    recent: string[];
    incidents?: IncidentRow[];
  } | null): void {
    if (!told) {
      this.selectionInspect.hidden = true;
      this.selectionInspect.replaceChildren();
      return;
    }
    this.selectionInspect.hidden = false;
    const rows: [string, string][] = [];
    rows.push(['driven by', told.player ? 'a player' : 'simulation']);
    if (told.kind === 'patrol') {
      rows.push(['lights', told.lights ? 'running' : 'off']);
      if (told.call) rows.push(['out on', told.call]);
    }
    if (told.headed) rows.push(['headed', told.headed]);
    if (told.wanted) rows.push(['hotlist', 'LISTED']);
    setRows(this.selectionInspect, rows);
    if (told.recent.length > 0) {
      const trail = document.createElement('div');
      trail.className = 'inspect-trail';
      for (const line of told.recent.slice(0, 5)) {
        const row = document.createElement('div');
        row.textContent = line;
        trail.append(row);
      }
      this.selectionInspect.append(trail);
    }

    // The driver record: the durable enforcement history for this plate, its
    // stops and pursuits, newest first. Admin-only -- it rides on the inspect
    // reply, which the server sends to the oversight seat alone, so a working
    // officer's selection never shows it.
    const record = told.incidents ?? [];
    if (record.length > 0) {
      const box = document.createElement('div');
      box.className = 'inspect-record';
      const head = document.createElement('div');
      head.className = 'inspect-record-head';
      head.textContent = 'driver record';
      box.append(head);
      for (const inc of record.slice(0, 6)) {
        const row = document.createElement('div');
        row.className = 'inspect-record-row';
        const badge = document.createElement('span');
        badge.className = `inspect-out ${inc.outcome.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
        badge.textContent = inc.outcome;
        const detail = document.createElement('span');
        detail.className = 'inspect-out-detail';
        detail.textContent = inc.detail;
        row.append(badge, detail);
        box.append(row);
      }
      this.selectionInspect.append(box);
    }
  }

  /** Told when the player closes the selected-vehicle panel. */
  private onDeselect: (() => void) | null = null;

  /** Called when the close control on the selection panel is pressed. */
  whenDeselected(listener: () => void): void {
    this.onDeselect = listener;
  }

  /**
   * Keep a message in the feed and repaint it. Identical repeats collapse rather
   * than stacking twenty "low fuel" lines. The panel reveals itself on the first
   * message and stays, live, so a player never has to go looking for it.
   */
  private pushLog(message: string): void {
    const last = this.logHistory[0];
    if (last && last.text === message) {
      last.at = performance.now();
    } else {
      this.logHistory.unshift({ text: message, at: performance.now() });
      if (this.logHistory.length > 100) this.logHistory.length = 100;
    }
    this.notesPanel.hidden = false;
    // Paused: bank it and count it, but leave the view where the reader put it.
    if (this.notesPaused) {
      this.notesPending++;
      this.notesPause.textContent = `▶ ${this.notesPending}`;
      return;
    }
    this.renderNotes();
  }

  private toggleNotesPause(): void {
    this.notesPaused = !this.notesPaused;
    this.notesPause.classList.toggle('on', this.notesPaused);
    if (this.notesPaused) {
      this.notesPause.textContent = '▶';
      this.notesPause.title = 'resume the feed';
    } else {
      this.notesPending = 0;
      this.notesPause.textContent = '⏸';
      this.notesPause.title = 'pause the feed';
      this.renderNotes();
    }
  }

  private renderNotes(): void {
    this.notesList.replaceChildren();
    const now = performance.now();
    for (const entry of this.logHistory) {
      const row = document.createElement('div');
      row.className = 'notes-row';
      const when = document.createElement('span');
      when.className = 'notes-when';
      when.textContent = ago((now - entry.at) / 1000);
      const text = document.createElement('span');
      text.className = 'notes-text';
      text.textContent = entry.text;
      row.append(when, text);
      this.notesList.append(row);
    }
  }

  constructor(county: County) {
    this.county = county;
    this.countyName.textContent = `${county.name}, ${county.state.name}`;
    this.buildLegend();
    this.buildHelp();

    this.notesPause.addEventListener('click', (event) => {
      event.stopPropagation();
      this.toggleNotesPause();
    });
    must('notes-clear').addEventListener('click', (event) => {
      event.stopPropagation();
      this.logHistory.length = 0;
      this.notesPending = 0;
      this.notesPanel.hidden = true;
    });

    // Selecting a car had no undo except Escape, which is no use on a phone.
    must('selection-close').addEventListener('click', () => this.onDeselect?.());

    /**
     * The left panels shrink, in steps, because on a phone they are a fifth of
     * the screen and the screen is the map. Full, then the essentials on one
     * line, then just the header. The header is always there so the way back
     * is always there.
     */
    for (const [id, panel] of [
      ['self-shrink', this.selfPanel],
      ['county-shrink', must('county-title').parentElement as HTMLElement],
      ['notes-shrink', this.notesPanel],
    ] as const) {
      must(id).addEventListener('click', (event) => {
        event.stopPropagation();
        if (panel.classList.contains('shut')) {
          panel.classList.remove('shut');
        } else if (panel.classList.contains('mini')) {
          panel.classList.remove('mini');
          panel.classList.add('shut');
        } else {
          panel.classList.add('mini');
        }
      });
    }

    /**
     * Clicking a plate copies it.
     *
     * The plate is the only handle this game gives you on a vehicle, and the
     * thing you do with one is type it into a search. Retyping seven
     * characters you are looking straight at, correctly, is exactly the
     * transcription the misread model is about -- and losing a search to your
     * own typo is a bad joke rather than a point.
     */
    for (const plate of [this.selectionPlate, this.selfPlate]) {
      plate.classList.add('plate-copy');
      plate.title = 'copy';
      plate.addEventListener('click', () => {
        const text = plate.textContent?.trim();
        if (!text) return;
        void navigator.clipboard?.writeText(text).then(
          () => this.say(`${text} copied.`, 3),
          () => this.say('This browser would not let go of the clipboard.', 4),
        );
      });
    }
  }

  /** The world's day length, once the server has said what it is. */
  /** Say which side is being played, which changes what the panels show. */
  setRole(enforcement: boolean): void {
    this.enforcement = enforcement;
  }

  setDaySeconds(seconds: number): void {
    if (seconds > 0) this.daySeconds = seconds;
  }

  /**
   * Repaint at 8 Hz. The numbers are being read, not watched, and rewriting
   * this much text every frame shows up in a profile for no benefit.
   */
  update(model: HudModel, now: number): void {
    if (now - this.lastPainted < 125) return;
    this.lastPainted = now;

    this.now = model.time;
    this.connectionDot.className = `dot ${model.connection}`;

    const rows: [string, string][] = [
      ['sim clock', formatClock(model.time, this.daySeconds)],
      ['tick', model.tick.toLocaleString()],
      ['speed', model.paused ? 'paused' : `${model.speed}x`],
    ];
    if (model.connection === 'solo') {
      rows.push(['mode', 'solo, no server']);
      rows.push(['vehicles', (model.total ?? model.visible).toLocaleString()]);
    } else {
      rows.push(['mode', model.connection]);
      rows.push(['ping', model.pingMs ? `${Math.round(model.pingMs)} ms` : '-']);
      // Not how many vehicles exist -- how many this client has been told
      // about, which is the whole point of interest management. It said "in
      // view", which reads as "on screen" and is not what it counts: a car two
      // streets away and behind you is in this number and not in view.
      rows.push(['traffic tracked', model.visible.toLocaleString()]);
    }
    if (model.connection !== 'solo') {
      rows.push(['plates legible', String(model.readable ?? 0)]);
      rows.push(['plates recalled', String(model.remembered ?? 0)]);
    }
    rows.push(['roads', `${this.county.edgeCount.toLocaleString()} edges`]);
    rows.push(['junctions', this.county.nodeCount.toLocaleString()]);
    rows.push(['fps', String(Math.round(model.fps))]);
    setRows(this.countyRows, rows);

    this.paintStatusLine(model.self);
    this.paintVehicle(this.selfPanel, this.selfPlate, this.selfRows, null, model.self, true);
    this.paintVehicle(
      this.selection,
      this.selectionPlate,
      this.selectionRows,
      this.selectionNote,
      model.selected,
      false,
    );
  }

  /**
   * The one-line version, for a screen with no room for the panel.
   *
   * Hidden by the stylesheet on anything wide enough for the real thing, so
   * this costs a desktop player nothing and is the entire heads-up display on a
   * phone.
   */
  private paintStatusLine(self: VehicleInfo | null): void {
    if (!self) {
      this.statusline.textContent = '';
      return;
    }
    const tag = self.expired ? 'TAG EXPIRED' : '';
    // Your own plate being on a list is a thing you know about yourself. It
    // reveals nothing: the network already has it and everybody else can read
    // the plate off the back of the car.
    const listed = this.wanted ? 'WANTED' : '';
    this.statusline.textContent = [self.plate, `${self.mph} mph`, self.road, tag, listed]
      .filter((part) => part !== '' && part !== undefined)
      .join('  ·  ');
    this.statusline.classList.toggle('expired', self.expired === true || this.wanted);
  }

  private paintVehicle(
    panel: HTMLElement,
    plateElement: HTMLElement,
    rowsElement: HTMLElement,
    note: HTMLElement | null,
    info: VehicleInfo | null,
    isSelf: boolean,
  ): void {
    if (!info) {
      panel.hidden = true;
      return;
    }
    panel.hidden = false;

    const unread = info.how === 'unread';
    const remembered = info.how === 'remembered';

    // On duty your unit is a government vehicle -- a gold plate with a call-sign,
    // not the civilian plate on your own car. Only your own panel: a car you have
    // selected still shows its real plate, which is the point of selecting it.
    const gov = isSelf && this.enforcement;
    plateElement.className =
      (unread ? 'plate unread' : remembered ? 'plate recalled' : 'plate') + (gov ? ' gov' : '');
    plateElement.textContent = unread ? 'NOT READ' : gov ? unitCallSign(info.plate) : info.plate;
    // Dress it like a real plate: a state header and a slogan strap, or the
    // government legend for a unit. Unread has neither -- you do not know them.
    const abbr = info.jurisdiction.match(/\(([A-Z]{2})\)/)?.[1] ?? '';
    const stateName = info.jurisdiction.replace(/\s*\(.*\)\s*/, '').trim().toUpperCase();
    plateElement.dataset.state = unread ? '' : gov ? 'OFFICIAL' : stateName;
    plateElement.dataset.strap = unread ? '' : gov ? 'GOVERNMENT' : stateSlogan(abbr);

    if (note) {
      note.hidden = !unread && !remembered;
      if (unread) {
        note.textContent =
          'Too far to read, or the wrong side of a block. Get within about 500 feet ' +
          'down the same road, or 100 feet across a corner.';
      } else if (remembered) {
        note.textContent =
          'From memory. This is what you saw when you read it, not what is true now. ' +
          'It goes in a few minutes.';
      }
    }

    const rows: [string, string][] = [];
    let mini = '';
    if (isSelf && this.enforcement) {
      // On patrol you are not reading your own tag, you are watching the road
      // you are working -- so the panel gives you its posted limit, which is the
      // number a speed trap is run against.
      if ((info.postedMph ?? 0) > 0) rows.push(['speed limit', `${info.postedMph} mph`]);
    } else {
      if (!unread) rows.push(['issued', info.jurisdiction]);
      // The tag. Shown for anybody whose plate you can read, including your own,
      // because the sticker faces outward and everybody can see everybody's.
      if (!unread) rows.push(['registration', info.expired ? 'EXPIRED' : 'current']);
    }
    if (remembered) {
      rows.push(['read', `${ago(info.agoSeconds ?? 0)} ago`]);
      rows.push(['was on', info.road]);
      rows.push(['was doing', `${info.mph} mph`]);
    } else {
      rows.push(['on', info.road]);
      // Already reads "in Waukesha" or "near Delafield", so the label would be
      // saying it twice and getting it wrong half the time.
      if (info.town) rows.push(['', info.town]);
      // A parked car is not on a road, so it has no road class, and a row
      // reading "class -" is a row that exists to say nothing.
      // What class of road this is, which is an operator's question rather
      // than a driver's: nobody driving to work thinks of themselves as being
      // on a tertiary. It stays on the enforcement side, where the whole point
      // is that road class is what a deployment is chosen by.
      if (info.roadClass && this.enforcement) {
        rows.push(['class', roadWords(info.roadClass)]);
      }
      rows.push(['speed', `${info.mph} mph`]);
      rows.push(['heading', compass(info.heading)]);
      // The one row that survives the panel's mini state: everything a driver
      // glances at, in shorthand, on a single line.
      if (isSelf) mini = `${info.mph} mph ${compass(info.heading)} · ${info.town || info.road}`;
    }
    if (isSelf) {
      // Where you are going, and only if you are going somewhere.
      //
      // These four used to be five rows that were always present, three of
      // them saying "none", "0 edges" and so on -- a panel that keeps a slot
      // warm for information it does not have is a panel somebody stops
      // reading. Nothing is shown about a journey that is not happening.
      // Only when there is somewhere you are being taken. Having edges left on
      // a route is not the same as having a destination -- taking the wheel
      // leaves the car finishing the road it is on with nowhere to be, and the
      // panel was reporting a distance and an arrival time under the word
      // "none".
      if (info.edgesLeft > 0 && info.metersLeft > 0 && info.destination && info.destination !== 'none') {
        rows.push(['destination', info.destination]);
        rows.push(['distance', sayDistance(info.metersLeft)]);
        rows.push(['drive time', sayDuration(info.secondsLeft)]);
        rows.push(['arriving', formatClock(this.now + info.secondsLeft, this.daySeconds)]);
      }
    }
    setRows(rowsElement, rows);

    /**
     * The mini state's one surviving line.
     *
     * The panel's rows all hide under `.mini`; this row carries the class that
     * keeps it visible, so shrinking the panel leaves speed, heading and where
     * -- the glance information -- on a single line.
     */
    if (isSelf && mini) {
      const short = document.createElement('div');
      short.className = 'row keep';
      const value = document.createElement('span');
      value.textContent = mini;
      short.append(value);
      rowsElement.append(short);
    }
  }

  /** Whether this player's own plate is on a list. */
  setWanted(wanted: boolean): void {
    this.wanted = wanted;
  }

  /**
   * The tank.
   *
   * On the dashboard rather than on the phone, because fuel is a fact about the
   * car in front of you and not something you look up. Named station when it is
   * getting low, because at that point what you want is somewhere to go.
   */
  setFuel(gallons: number, tank: number, nearest?: { name: string; away: number }): void {
    if (tank <= 0) {
      this.fuel.hidden = true;
      return;
    }
    this.fuel.hidden = false;
    const share = Math.max(0, Math.min(1, gallons / tank));
    // The needle sweeps 120 degrees, E at the left and F at the right.
    this.fuelNeedle.style.transform = `rotate(${(-60 + share * 120).toFixed(1)}deg)`;
    this.fuel.classList.toggle('low', share <= FUEL_LOW);
    this.fuel.classList.toggle('empty', gallons <= 0.05);

    if (gallons <= 0.05) {
      this.fuelSaid.textContent = nearest
        ? `out of fuel · ${nearest.name} ${sayDistance(nearest.away)}`
        : 'out of fuel';
    } else if (share <= FUEL_LOW && nearest) {
      this.fuelSaid.textContent =
        `${nearest.name}, ${sayDistance(nearest.away)}`;
    } else {
      // A gauge does not need a percentage next to it, so this is blank unless
      // there is something to say.
      this.fuelSaid.textContent = '';
    }
  }

  say(message: string, seconds = 6): void {
    this.pushLog(message);
    this.toast.textContent = message;
    this.toast.hidden = false;
    this.toast.classList.remove('fading');
    window.clearTimeout(this.toastTimer);
    this.toastTimer = window.setTimeout(() => {
      this.toast.classList.add('fading');
      window.setTimeout(() => {
        this.toast.hidden = true;
      }, 250);
    }, seconds * 1000);
  }

  private buildHelp(): void {
    setRows(
      must('help-rows'),
      [
        ['click a road', 'drive there'],
        ['click a car', 'select it'],
        ['drag', 'pan'],
        ['wheel', 'zoom'],
        ['f', 'follow'],
        ['space', 'pause / stop'],
        ['x', 'pull the selected car over'],
        ['a', 'route around cameras you found'],
        ['c', 'cover the nearest camera'],
        ['v', 'break it (a service call)'],
        ['b', 'take it (comes back elsewhere)'],
        ['r', 'run (administrator)'],
        ['m', 'share your map, see the community one'],
        ['n', 'shared network on / off'],
        ['q', 'phone / terminal, up and down'],
        ['esc', 'put it down, or clear selection'],
      ],
      true,
    );

    // The key list opens on request. It used to be pinned to the bottom of a
    // column that overflowed, which is exactly where an overflowing column
    // loses things first.
    const toggle = must('help-toggle') as HTMLButtonElement;
    toggle.addEventListener('click', () => {
      this.help.hidden = !this.help.hidden;
      toggle.classList.toggle('open', !this.help.hidden);
    });
  }

  private buildLegend(): void {
    const present = new Set<string>();
    for (let e = 0; e < this.county.edgeCount; e++) {
      present.add(className(this.county.edgeClass[e] as number));
    }
    this.legend.replaceChildren();
    for (const cls of ROAD_CLASSES) {
      if (!present.has(cls)) continue;
      const row = document.createElement('div');
      row.className = 'legend-row';
      const swatch = document.createElement('span');
      swatch.className = 'legend-swatch';
      swatch.style.background = `#${ROAD_COLOR[cls].toString(16).padStart(6, '0')}`;
      const label = document.createElement('span');
      // The legend names roads the way a driver would, not the way OSM tags them.
      label.textContent = roadWords(cls);
      row.append(swatch, label);
      this.legend.append(row);
    }
  }
}

function setRows(host: HTMLElement, rows: [string, string][], boldFirst = false): void {
  // Reuse the existing row elements when the shape has not changed, so the
  // panel does not rebuild its DOM eight times a second.
  if (host.childElementCount !== rows.length) {
    host.replaceChildren();
    for (const _ of rows) {
      const row = document.createElement('div');
      row.className = 'row';
      row.append(
        document.createElement(boldFirst ? 'b' : 'span'),
        document.createElement('span'),
      );
      host.append(row);
    }
  }
  rows.forEach(([label, value], index) => {
    const row = host.children[index] as HTMLElement;
    // Named so a stylesheet can decide which of these matter on a phone. A
    // small screen has room for the plate and whether you are moving; it does
    // not have room for "route left, 0 edges", and choosing between them is a
    // layout decision rather than a rendering one.
    row.dataset.key = label.replace(/\s+/g, '-');
    (row.children[0] as HTMLElement).textContent = label;
    (row.children[1] as HTMLElement).textContent = value;
  });
}

/** How long ago, in the way a person would say it. */
function ago(seconds: number): string {
  const whole = Math.floor(seconds);
  if (whole < 60) return `${whole}s`;
  return `${Math.floor(whole / 60)}m ${String(whole % 60).padStart(2, '0')}s`;
}

const COMPASS = ['E', 'NE', 'N', 'NW', 'W', 'SW', 'S', 'SE'];

function compass(heading: number): string {
  const index = Math.round((heading / (Math.PI * 2)) * 8 + 8) % 8;
  return COMPASS[index] as string;
}

function must(id: string): HTMLElement {
  const element = document.getElementById(id);
  if (!element) throw new Error(`missing HUD element #${id}`);
  return element;
}

/**
 * A stable unit call-sign for an on-duty officer, derived from the plate on
 * their own car so it never moves between sessions but is plainly not that
 * plate. Two letters and four digits, the shape the county's marked units wear.
 */
function unitCallSign(plate: string): string {
  let h = 0;
  for (const c of plate) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  const letters = 'ABCDEFGHJKLMNPRSTUVWXYZ';
  const a = letters[h % letters.length] as string;
  const b = letters[(Math.floor(h / 23)) % letters.length] as string;
  return `${a}${b}-${String(h % 10000).padStart(4, '0')}`;
}
